// RecordingControls index.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStopCircle, faTimesCircle, faDownload, faPauseCircle, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import ConfirmationDialog from 'web/components/ConfirmationDialog';
import { DataPackageBuilder } from 'web/utils/DataPackageBuilder';
import { collectDeviceMetadata } from 'web/utils/DeviceMetadataUtil';
import { useAddLabelIntake, useUploadStream, useGetPatientMedicalTemplates } from 'web/services/api/queries';
import useEnsureDevice from 'web/hooks/device/useEnsureDevice';
import useAudioRecorder, { MicrophoneErrorType, timeSliceInSeconds } from 'web/hooks/audio/useAudioRecorder';
import { Label, LabelType } from 'web/model/Label';
import { useIntakeStore } from 'web/hooks/useIntakeStore';
import styles from './index.module.scss';
import classNames from 'classnames';
import * as Sentry from '@sentry/react';
import { useCurrentOrganization } from 'web/hooks/useCurrentOrganization';
import { Button } from 'react-daisyui';
import { useQueryClient } from 'react-query';
import { MicrophoneErrorDialog } from 'web/components/audio-recordings/MicrophoneErrorDialog';


const RecordingControls = () => {
    const { deviceSerial, isLoading: isLoadingDevice, error: errorLoadingDevice } = useEnsureDevice();
    const { consentGiven, label, cancelAdd, resetIntakeState, setRecording } = useIntakeStore();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const uploadStream = useUploadStream();
    const isGotLastChunk = useRef<boolean>(false);
    const isRequestedToStopRecording = useRef<boolean>(false);
    const isEnableDownload = useRef(false);
    const [downloadEnabled, setDownloadEnabled] = useState(isEnableDownload.current);
    const { mutate: addLabelIntake, isLoading: isAddingLabel } = useAddLabelIntake();
    const isRecordingStarted = useRef(false);
    const labelStartTime = useRef<number | null>(null);
    const streamStartTime = useRef<number | null>(null);
    const streamEndTime = useRef<number | null>(null);
    const labelEndTime = useRef<number | null>(null);
    const queryClient = useQueryClient();
    const [recordingTime, setRecordingTime] = useState<number>(0);
    const recordingTimeRef = useRef(recordingTime);
    recordingTimeRef.current = recordingTime;
    const isAddingLabelRef = useRef(false);

    const [isMuted, setIsMuted] = useState(false);
    const [volumeLevel, setVolumeLevel] = useState(0);
    const [isSoundLevelTooHigh, setIsSoundLevelTooHigh] = useState(false);
    const includeTodaysData = useRef(true);
    const [showMicrophoneError, setShowMicrophoneError] = useState(false);

    // Health monitoring state
    const uploadFailureCount = useRef(0);
    const lastSuccessfulUpload = useRef<number>(Date.now());
    const lastChunkReceived = useRef<number>(Date.now());
    const [isUploadHealthy, setIsUploadHealthy] = useState(true);
    const [recordingHealthError, setRecordingHealthError] = useState<string | null>(null);

    const phoneAudioRecording = 512;

    const { currentOrganization } = useCurrentOrganization();
    const { data: medicalTemplates } = useGetPatientMedicalTemplates(currentOrganization?.id ?? undefined, label?.patient?.id ?? label?.patientId ?? undefined);
    const selectedTemplate = useRef('');
    const [selectedTemplateState, setSelectedTemplateState] = useState('');

    useEffect(() => {
        if (medicalTemplates?.length) {
            const currentTemplate = medicalTemplates.find(template => template.isCurrent);
            if (currentTemplate) {
                selectedTemplate.current = currentTemplate.id || '';
                setSelectedTemplateState(currentTemplate.id || '');
            }
        }
    }, [medicalTemplates]);

    useEffect(() => {
        if (label?.patient?.isDefault === false) {
            includeTodaysData.current = true;
        } else {
            includeTodaysData.current = false;
        }
    }, [label?.patient?.isDefault]);

    const handleCancel = useCallback(() => {
        setIsConfirmOpen(true);
    }, []);

    const denyCancel = useCallback(() => {
        setIsConfirmOpen(false);
    }, []);

    // Forward declare functions we'll define later
    const resetRecordingState = useCallback(() => {
        isRecordingStarted.current = false;
        setRecordingTime(0);
        cancelAdd();
        resetIntakeState();
    }, [cancelAdd, resetIntakeState]);

    // First define the minimal stub for handleLastRecordingChunk that we need for processPCM
    // We'll redefine it fully later
    let handleLastRecordingChunk = () => {};

    const processPCM = useCallback((blob: Blob) => {
        if (isGotLastChunk.current) {
            return;
        }

        // Update last chunk received timestamp
        lastChunkReceived.current = Date.now();

        blob.arrayBuffer().then(buffer => {
            const byteLength = buffer.byteLength;

            // Define a maximum allowable buffer size (in bytes)
            const MAX_BUFFER_SIZE = 1024 * 1024 * 5; // 5 MB as an limit

            // Ensure the buffer length is valid and not too large
            if (byteLength <= 0) {
                const errorMsg = `Error: Buffer length is invalid (${byteLength})`;
                console.error(errorMsg);

                // Log this error to Sentry
                Sentry.captureMessage(errorMsg, 'error');

                return;
            }

            if (byteLength > MAX_BUFFER_SIZE) {
                const errorMsg = `Error: Buffer length exceeds maximum allowed size (${byteLength} > ${MAX_BUFFER_SIZE})`;
                console.error(errorMsg);

                // Log this error to Sentry with additional context if needed
                Sentry.captureMessage(errorMsg, {
                    level: 'warning',
                    extra: {
                        byteLength,
                        maxBufferSize: MAX_BUFFER_SIZE,
                    }
                });

                return;
            }


            const int16Array = new Int16Array(buffer);

            const calculatedDiff = Math.round((int16Array.length / sampleRate) * 1000);

            if (isRequestedToStopRecording.current) {
                isGotLastChunk.current = true;
            }

            if (streamStartTime.current == null) {
                streamStartTime.current = Date.now() - calculatedDiff;
                labelStartTime.current = streamStartTime.current;
            }

            if (streamEndTime.current != null) {
                streamStartTime.current = streamEndTime.current;
            }

            streamEndTime.current = streamStartTime.current + calculatedDiff;
            labelEndTime.current = streamEndTime.current + calculatedDiff;

            let dataPackage = new DataPackageBuilder(BigInt(streamStartTime.current), BigInt(streamEndTime.current + 5), [phoneAudioRecording]);
            int16Array.forEach(sample => dataPackage.addSample(sample));

            // Check if mic is muted using threshold approach
            const zeroSampleCount = int16Array.filter(sample => sample === 0).length;
            const zeroSamplePercentage = (zeroSampleCount / int16Array.length) * 100;
            const mutedThresholdPercentage = 95; // Define the threshold percentage
            const isDataMuted = zeroSamplePercentage > mutedThresholdPercentage;
            setIsMuted(isDataMuted);

            // Check if sound level too high
            const SoundLevelHighThreshold = 0.98 * 32768; // 98% of max value
            const isDataSoundHigh = int16Array.some(sample => Math.abs(sample) > SoundLevelHighThreshold);
            setIsSoundLevelTooHigh(isDataSoundHigh);

            if (!isDataMuted) {
                // Calculate average volume level using RMS
                const sumOfSquares = int16Array.reduce((sum, sample) => sum + sample * sample, 0);
                const rms = Math.sqrt(sumOfSquares / int16Array.length);
                const volume = rms / 32768; // Normalize to range 0-1
                setVolumeLevel(volume);
            } else {
                setVolumeLevel(0);
            }

            setTimeout(async () => {
                try {
                    const fileContents = dataPackage.build();
                    await uploadStream.mutateAsync({ deviceSerial, fileContents });

                    // Reset failure count on success
                    uploadFailureCount.current = 0;
                    lastSuccessfulUpload.current = Date.now();
                    setIsUploadHealthy(true);
                    setRecordingHealthError(null);
                } catch (error) {
                    console.error('Exception in uploading stream', error);
                    uploadFailureCount.current++;

                    // If 3+ consecutive failures, mark upload as unhealthy
                    if (uploadFailureCount.current >= 3) {
                        setIsUploadHealthy(false);
                        setRecordingHealthError('Failed to upload recording data to server');
                    }
                }

                // Use flag to ensure only one call to handleLastRecordingChunk
                if (isRequestedToStopRecording.current && !isAddingLabelRef.current) {
                    setRecording(false);
                    handleLastRecordingChunk();
                }
            }, 0);

        }).catch(console.error);
    }, [uploadStream, deviceSerial]);

    const {
        startRecording,
        stopRecording,
        togglePauseResume,
        isRecording,
        isPaused,
        sampleRate,
        microphoneError
    } = useAudioRecorder({ processPCM });

    // Watchdog timer to monitor that processPCM is being called regularly
    useEffect(() => {
        if (!isRecording || isPaused) return;

        const watchdog = setInterval(() => {
            const timeSinceLastChunk = Date.now() - lastChunkReceived.current;
            const expectedInterval = timeSliceInSeconds * 1000;

            // If no chunk received in 2.5x expected interval, something is wrong
            if (timeSinceLastChunk > expectedInterval * 2.5) {
                console.error('No audio chunks received - recording may have failed');
                setRecordingHealthError('No audio data being received from microphone');
                setShowMicrophoneError(true);
            }
        }, 5000); // Check every 5 seconds

        return () => clearInterval(watchdog);
    }, [isRecording, isPaused]);

    // Monitor upload health - removed auto-cancel behavior, user decides via dialog

    // Now we can properly define handleLastRecordingChunk with all dependencies
    handleLastRecordingChunk = useCallback(() => {
        // Guard against multiple calls
        if (isAddingLabelRef.current) {
            console.log('Already adding label, skipping...');
            return;
        }

        // Set the flag immediately to prevent concurrent calls
        isAddingLabelRef.current = true;

        try {
            stopRecording(isEnableDownload.current);
        } catch (error) {
            console.error('Error stopping recording in handleLastRecordingChunk:', error);
            // Continue with the label creation even if stopRecording fails
        }
        const recordingStopTime = Date.now();

        let newLabelType = label?.type ?? "NotSpecified";

        if (label?.type === "ClinicConversation" && includeTodaysData.current) {
            newLabelType = "CombinedClinicConv";
        }

        // Collect device metadata for the label
        const deviceMetadata = collectDeviceMetadata();

        const newLabel = new Label({
            ...label,
            type: newLabelType,
            fromTime: labelStartTime.current ?? recordingStopTime,
            toTime: labelEndTime.current ?? recordingStopTime,
            color: "PURPLE",
            description: "",
            medicalTemplateId: selectedTemplate.current || undefined,
            metadata: deviceMetadata, // Add device metadata to the label
        });

        addLabelIntake({
            deviceSerial: deviceSerial,
            label: newLabel
        }, {
            onSuccess: () => {
                resetRecordingState();
                // Invalidate the recordings query so that it refetches
                queryClient.invalidateQueries([
                    'organization-labels',
                    currentOrganization?.id,
                    {
                        types: [LabelType[LabelType.CombinedClinicConv], LabelType[LabelType.ClinicConversation], LabelType[LabelType.DictationAudio]],
                        limit: 20,
                        sortingProperty: 'FromTime',
                        isSortingDescending: true,
                        offset: 0,
                    }
                ]);
            },
            onError: (e: Error) => {
                resetRecordingState();
                console.error('Upload error', e);
            },
        });
    }, [label, stopRecording, labelStartTime, labelEndTime, addLabelIntake, deviceSerial, isEnableDownload, selectedTemplate, resetRecordingState, queryClient, currentOrganization?.id]);

    // Now define confirmCancel with all its dependencies available
    const confirmCancel = useCallback(() => {
        setIsConfirmOpen(false);
        stopRecording(false);
        resetRecordingState();
    }, [stopRecording, resetRecordingState]);

    // Monitor for microphone errors
    useEffect(() => {
        if (microphoneError) {
            setShowMicrophoneError(true);
            // If we have a microphone error while attempting to record, reset recording state
            if (isRecordingStarted.current) {
                resetRecordingState();
            }
        }
    }, [microphoneError, resetRecordingState]);

    const handleCloseMicrophoneError = useCallback(() => {
        setShowMicrophoneError(false);

        // If this is a health error and user chose to cancel (close dialog), stop recording without saving
        if (recordingHealthError && isRecording) {
            try {
                stopRecording(false);
            } catch (error) {
                console.error('Error stopping recording during cancel:', error);
            }
            resetRecordingState();
        }
    }, [recordingHealthError, isRecording, stopRecording, resetRecordingState]);

    const handleStopRecording = useCallback(() => {
        try {
            setRecording(false);
            isRequestedToStopRecording.current = true;

            if (isPaused) {
                // If paused, we won't get more chunks, so handle the last one directly
                if (!isAddingLabelRef.current) {
                    isGotLastChunk.current = true;
                    handleLastRecordingChunk();
                }
            }
        } catch (error) {
            console.error('Error in handleStopRecording:', error);
            // Ensure we still try to clean up the state even if there's an error
            setRecording(false);
            isRequestedToStopRecording.current = true;
            // Force the label to be added if we haven't already
            if (!isAddingLabelRef.current) {
                isGotLastChunk.current = true;
                handleLastRecordingChunk();
            }
        }
    }, [isPaused, handleLastRecordingChunk]);

    // Handler specifically for health errors - forces save immediately
    const handleStopAndSaveRecording = useCallback(() => {
        try {
            console.log('Force stopping and saving recording due to health error');
            setRecording(false);
            isRequestedToStopRecording.current = true;
            isGotLastChunk.current = true;

            // Force the label to be saved immediately, regardless of recording state
            if (!isAddingLabelRef.current) {
                handleLastRecordingChunk();
            }

            // Close the error dialog
            setShowMicrophoneError(false);
            setRecordingHealthError(null);
        } catch (error) {
            console.error('Error in handleStopAndSaveRecording:', error);
            // Ensure we still try to save even if there's an error
            if (!isAddingLabelRef.current) {
                isGotLastChunk.current = true;
                handleLastRecordingChunk();
            }
            setShowMicrophoneError(false);
            setRecordingHealthError(null);
        }
    }, [handleLastRecordingChunk]);

    const handleStartRecording = useCallback(() => {
        if (isAddingLabel || isLoadingDevice || errorLoadingDevice) return;

        // Reset the flag for the new recording session
        isAddingLabelRef.current = false;

        streamStartTime.current = null;
        labelStartTime.current = null;
        isRequestedToStopRecording.current = false;
        isGotLastChunk.current = false;
        streamEndTime.current = null;

        // Reset health monitoring state
        uploadFailureCount.current = 0;
        lastSuccessfulUpload.current = Date.now();
        lastChunkReceived.current = Date.now();
        setIsUploadHealthy(true);
        setRecordingHealthError(null);

        startRecording();
    }, [startRecording, isAddingLabel, isLoadingDevice, errorLoadingDevice]);

    const toggleDownload = () => {
        isEnableDownload.current = !isEnableDownload.current;
        setDownloadEnabled(isEnableDownload.current);
    };

    useEffect(() => {
        if (!consentGiven || !label || isLoadingDevice || isRecordingStarted.current) {
            return;
        }

        isRecordingStarted.current = true;
        handleStartRecording();
    }, [consentGiven, label, isLoadingDevice, handleStartRecording]);

    useEffect(() => {
        setRecording(isRecording);
        let interval: NodeJS.Timer;
        let maxRecordingTimeInSeconds = 3600;
        if (isRecording && !isPaused) {
            interval = setInterval(() => {
                if (recordingTimeRef.current >= maxRecordingTimeInSeconds) {
                    handleStopRecording();
                } else {
                    setRecordingTime(prevTime => prevTime + 1);
                }
            }, 1000);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [isRecording, isPaused, handleStopRecording]);

    // Add cleanup effect to reset flag on unmount
    useEffect(() => {
        return () => {
            // Reset the flag on component unmount
            isAddingLabelRef.current = false;
        };
    }, []);

    if (isConfirmOpen) {
        return <ConfirmationDialog
            onConfirm={confirmCancel}
            onCancel={denyCancel}
            confirmButtonText="Yes"
            cancelButtonText="No"
            title="Confirm Cancellation"
            message="Are you sure you want to exit? Your recording hasn't been saved and will be lost."
        />;
    }

    if (!isRecording && !isPaused && !showMicrophoneError) {
        return null;
    }

    return (
        <>
            {showMicrophoneError && (
                <MicrophoneErrorDialog
                    error={recordingHealthError ? { type: MicrophoneErrorType.GENERAL_ERROR, message: recordingHealthError } : microphoneError}
                    isOpen={showMicrophoneError}
                    onClose={handleCloseMicrophoneError}
                    onStopAndSave={recordingHealthError ? handleStopAndSaveRecording : undefined}
                    isHealthError={!!recordingHealthError}
                />
            )}
            
            <div className="fixed top-0 right-0 bg-white p-4 shadow-lg flex flex-col items-end mr-4 mt-4 z-40">
                <div className="flex justify-between items-start w-full mb-6">
                    {/* Left side - Template controls */}
                    <div className="flex flex-col gap-3 flex-1">
                        {label?.type === "ClinicConversation" && !label?.patient?.isDefault && (
                            <label className="inline-flex items-center text-sm text-gray-700 font-medium">
                                <input
                                    type="checkbox"
                                    className="form-checkbox h-5 w-5 text-blue-600"
                                    defaultChecked={true}
                                    checked={includeTodaysData.current}
                                    onChange={(e) => (includeTodaysData.current = e.target.checked)}
                                />
                                <span className="ml-2">Include Today's Conversations</span>
                            </label>
                        )}

                        {medicalTemplates && medicalTemplates.length > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-700 whitespace-nowrap">Template:</span>
                                <select
                                    className="border rounded-md py-2 px-3 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                    value={selectedTemplateState}
                                    onChange={(e) => {
                                        selectedTemplate.current = e.target.value;
                                        setSelectedTemplateState(e.target.value);
                                    }}
                                >
                                    {medicalTemplates
                                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                        .slice(0, 15)
                                        .map(template => (
                                            <option key={template.id} value={template.id}>
                                                {template.content.name} {template.isCurrent ? '(Current)' : ''}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Right side - Recording time */}
                    <div className="flex-shrink-0 ml-8 px-4 py-3 bg-gray-50 rounded-lg shadow-md">
                        <div className="text-sm font-medium text-gray-700">
                            Recording Time: 
                            <span className="ml-2 font-mono text-blue-600">
                                {new Date(recordingTime * 1000).toISOString().substr(11, 8)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex space-x-3 mt-3">
                    <Button onClick={handleStopRecording} className="bg-red-500 rounded-lg hover:bg-red-700 text-white font-bold py-2 px-4 inline-flex items-center shadow-md transition-all duration-300">
                        <FontAwesomeIcon icon={faStopCircle} className="mr-2" /> Stop
                    </Button>
                    <Button onClick={togglePauseResume} className={`bg-black rounded-lg hover:bg-[#2f2f2f] text-white font-bold py-2 px-4 inline-flex items-center shadow-md transition-all duration-300`}>
                        <FontAwesomeIcon icon={isPaused ? faPlayCircle : faPauseCircle} className="mr-2" /> {isPaused ? 'Resume' : 'Pause'}
                    </Button>
                    <Button onClick={handleCancel} className="bg-gray-100 rounded-lg hover:bg-gray-200 font-bold py-2 px-4 inline-flex items-center shadow-md transition-all duration-300">
                        <FontAwesomeIcon icon={faTimesCircle} className="mr-2" /> Cancel
                    </Button>
                    <Button
                        onClick={toggleDownload}
                        className={`font-bold py-2 px-4 rounded-lg inline-flex items-center ${downloadEnabled ? 'bg-red-500 hover:bg-red-700' : 'bg-[#26818E] hover:bg-[#72AFB8]'} text-white shadow-md transition-all duration-300`}
                        aria-pressed={downloadEnabled}
                        aria-label={downloadEnabled ? 'Disable automatic downloads' : 'Enable automatic downloads'}
                    >
                        <FontAwesomeIcon icon={downloadEnabled ? faTimesCircle : faDownload} className="mr-2" />
                        {downloadEnabled ? 'Disable Auto Download' : 'Enable Auto Download'}
                    </Button>
                </div>

                <div className="mt-4 flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                        {isMuted ? (
                            <>
                                <div className={classNames(styles['blinking-red-light'])}></div>
                                <span className="text-red-500 font-bold">Microphone is muted</span>
                            </>
                        ) : (
                            <>
                                {volumeLevel < 0.012 ? (
                                    <>
                                        <div className={classNames(styles['volume-bar'], { [styles['low-volume']]: volumeLevel < 0.1 })} style={{ width: `${volumeLevel * 100}%` }}></div>
                                        <span className="text-yellow-500 font-bold whitespace-nowrap">Low or No Audio Detected</span>
                                    </>
                                ) : (
                                    isSoundLevelTooHigh && (
                                        <span className="text-red-500 font-bold whitespace-nowrap">Sound Level Too High</span>
                                    )
                                )}
                            </>
                        )}
                    </div>

                    {/* Upload health indicator */}
                    <div className="flex items-center space-x-2">
                        {!isUploadHealthy && recordingHealthError ? (
                            <>
                                <div className={classNames(styles['blinking-red-light'])}></div>
                                <span className="text-red-500 font-bold whitespace-nowrap">{recordingHealthError}</span>
                            </>
                        ) : isUploadHealthy && isRecording && !isPaused ? (
                            <>
                                <span className="text-green-500 text-sm whitespace-nowrap">● Streaming to server</span>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default RecordingControls;